import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ExternalLink, Play, X } from 'lucide-react'
import { api } from '@/lib/api'
import { useT } from '@/lib/i18n'

interface YouTubeVideo {
  video_id: string
  title: string
  thumbnail_url: string
  published_at: string
}

interface Props {
  channelId: string
  limit?: number
}

/**
 * Horizontal-scroll carousel of a YouTube channel's latest uploads.
 * Hovering a thumbnail shows a quick muted preview on desktop.
 * Clicking a video opens a full modal player with sound (unmuted) and
 * includes a direct link to watch on YouTube.
 */
export function YouTubeCarousel({ channelId, limit = 8 }: Props) {
  const [hoveredId, setHoveredId] = useState<string | null>(null)
  const [selectedVideo, setSelectedVideo] = useState<YouTubeVideo | null>(null)
  const T = useT('common')

  const { data: videos } = useQuery<YouTubeVideo[]>({
    queryKey: ['youtube-latest', channelId, limit],
    queryFn: () =>
      api.get<YouTubeVideo[]>(
        `/youtube/latest?channel_id=${encodeURIComponent(channelId)}&limit=${limit}`,
      ),
    enabled: Boolean(channelId),
    staleTime: 15 * 60_000,
  })

  useEffect(() => {
    if (!selectedVideo) return
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setSelectedVideo(null)
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [selectedVideo])

  if (!channelId || !videos || videos.length === 0) return null

  return (
    <>
      <div className="flex snap-x snap-mandatory gap-3 overflow-x-auto px-1 pb-2">
        {videos.map((video) => {
          const isHovered = hoveredId === video.video_id
          return (
            <button
              key={video.video_id}
              type="button"
              onClick={() => {
                setHoveredId(null)
                setSelectedVideo(video)
              }}
              onMouseEnter={() => setHoveredId(video.video_id)}
              onMouseLeave={() => setHoveredId((current) => (current === video.video_id ? null : current))}
              aria-label={video.title}
              className="group relative block h-32 w-56 shrink-0 snap-start overflow-hidden rounded-2xl bg-black text-left shadow-sm focus:outline-none focus:ring-2 focus:ring-rose-500"
            >
              {isHovered ? (
                <iframe
                  src={`https://www.youtube.com/embed/${video.video_id}?autoplay=1&mute=1&controls=0&modestbranding=1&rel=0&playsinline=1`}
                  title={video.title}
                  allow="autoplay; encrypted-media"
                  className="h-full w-full pointer-events-none"
                />
              ) : (
                <>
                  <img
                    src={video.thumbnail_url}
                    alt={video.title}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20 transition-colors group-hover:bg-black/35">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-rose-600/90 text-white shadow-lg transition-transform duration-200 group-hover:scale-110">
                      <Play className="ml-0.5 h-5 w-5 fill-current" />
                    </div>
                  </div>
                  <p className="absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-black/90 via-black/50 to-transparent px-2.5 py-2 text-[11px] font-medium text-white">
                    {video.title}
                  </p>
                </>
              )}
            </button>
          )
        })}
      </div>

      {selectedVideo && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={selectedVideo.title}
          onClick={() => setSelectedVideo(null)}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4 backdrop-blur-sm"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative flex w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-slate-800 bg-slate-950 shadow-2xl"
          >
            <div className="flex items-center justify-between border-b border-slate-800/80 px-4 py-3 text-slate-200">
              <h3 className="truncate pr-3 text-sm font-semibold">{selectedVideo.title}</h3>
              <button
                type="button"
                onClick={() => setSelectedVideo(null)}
                aria-label={T.close}
                className="rounded-lg p-1 text-slate-400 transition hover:bg-slate-800 hover:text-slate-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="relative aspect-video w-full bg-black">
              <iframe
                src={`https://www.youtube.com/embed/${selectedVideo.video_id}?autoplay=1&mute=0&controls=1&rel=0&playsinline=1`}
                title={selectedVideo.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="h-full w-full border-0"
              />
            </div>

            <div className="flex items-center justify-between border-t border-slate-800/80 px-4 py-3">
              <span className="text-xs text-slate-400">YouTube</span>
              <a
                href={`https://www.youtube.com/watch?v=${selectedVideo.video_id}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-rose-500"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                {T.watchOnYoutube}
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
